// Copied from frontend/src/components/PowerFlowGraphic.tsx — keep in sync manually.
import type { ReactNode } from "react";
import { useLayoutEffect, useRef, useState } from "react";
import { getTheme } from "../lib/theme";

// ---------------------------------------------------------------------------
// Dynamic power-flow graphic (Dashboard -> Graphics).
//
// Layout is described in a fixed SVG viewBox coordinate space (VW x VH). The
// central inverter is a circle; each surrounding component ("spoke") is a
// rounded card connected to the inverter by a power line. The line leaves the
// card horizontally (side cards) or vertically (bottom cards) and meets the
// circle radially (perpendicular to the circumference) in the matching color.
//
// HTML cards are overlaid on top of the SVG using percentage positions derived
// from the same coordinate space so they stay aligned at any size.
// ---------------------------------------------------------------------------

export const FLOW_COLORS = {
  green: "#22c55e",
  red: "#ef4444",
  blue: "#38bdf8",
  orange: "#f59e0b",
  grey: "#64748b",
};

/**
 * "Off" / low-power gray, per theme. Light mode uses a lighter gray so the
 * inactive components stay subtle against the bright background; dark mode
 * keeps the deeper slate gray.
 */
export const OFF_COLORS = {
  dark: "#64748b",
  light: "#cbd5e1",
};

/** Resolve the off-color for the currently active theme. */
function offColor(): string {
  return getTheme() === "light" ? OFF_COLORS.light : OFF_COLORS.dark;
}

/**
 * CSS filter that tints a colored image toward the off-color. Desaturates the
 * image, then recolors it to the slate (dark) / light-gray (light) off hue.
 */
function offFilter(): string {
  return getTheme() === "light"
    ? "grayscale(1) brightness(1.35) opacity(0.75)"
    : "grayscale(1) sepia(1) hue-rotate(175deg) saturate(0.35) brightness(0.95) opacity(0.85)";
}

export type SlotId = "pv" | "grid" | "battery" | "generator" | "house" | "relay1" | "relay2";

export interface Spoke {
  id: SlotId;
  title: string;
  /** Formatted main value, e.g. "4.20 kW". */
  value: string;
  /** Action / mode label, e.g. POWER / CHARGING / EXPORT / LOAD. */
  action: string;
  /** Optional extra detail (e.g. battery SoC "85%" + charge/discharge ETA). */
  extra?: ReactNode;
  /** Line + accent color (hex). */
  color: string;
  /** Whether the line animates and the card pings. */
  active: boolean;
  /** Animation direction along the line: toward ("in") or away ("out"). */
  direction: "in" | "out";
  /** Power magnitude in W (controls ping cadence). */
  power: number;
  /** Placeholder icon (real images added later). */
  icon: ReactNode;
  /** Displayed image width in viewBox units (card width = imgW + 100). */
  imgW: number;
  /** Optional status badge overlaid on the card's top-right corner. */
  badge?: ReactNode;
}

export interface PowerFlowGraphicProps {
  spokes: Spoke[];
  inverter: {
    value: string;
    label: string;
    state: string;
    color?: string;
    animate?: boolean;
    icon?: ReactNode;
    /** Inverter power history (magnitude in W per bucket); drawn inside the circle. */
    history?: number[];
  };
  /**
   * Optional per-spoke power history (one magnitude in W per time bucket, oldest
   * first). When present, a translucent usage sparkline is drawn at the bottom
   * of the matching card. Only supplied in the large Dashboard -> Graphics view.
   */
  history?: Partial<Record<SlotId, number[]>>;
}

// -- geometry ---------------------------------------------------------------

const VW = 1400;
const VH = 750;
const CX = 700;
const CY = 300;
const R = 150;

// evi1m3
const CARD_H = 160;
// Card width = image width + this (10px left pad + 10px gap + 80 text area).
const CARD_EXTRA = 180;

type Side = "left" | "right" | "bottom";

interface SlotGeom {
  /**
   * Anchor X in viewBox units. Meaning depends on `side`:
   * left -> card left edge, right -> card right edge, bottom -> card center X.
   */
  anchor: number;
  /** Card top edge in viewBox units. */
  y: number;
  side: Side;
  /** Entry angle on the circle, degrees (0 = right, 90 = down). */
  angle: number;
}

const SLOTS: Record<SlotId, SlotGeom> = {
  pv: { anchor: 40, y: 36, side: "left", angle: 214 },
  grid: { anchor: VW - 40, y: 36, side: "right", angle: 326 },
  battery: { anchor: 40, y: 252, side: "left", angle: 180 },
  generator: { anchor: VW - 40, y: 252, side: "right", angle: 0 },
  house: { anchor: 250, y: 558, side: "bottom", angle: 126 },
  relay1: { anchor: CX + 50, y: 558, side: "bottom", angle: 92 },
  relay2: { anchor: VW - 150, y: 558, side: "bottom", angle: 54 },
};

/** Below this power (W) a component (and its line) is shown in gray. */
const LOW_POWER_W = 100;

/** Effective color: gray when power is below the low-power threshold. */
function effColor(spoke: Spoke): string {
  return spoke.power < LOW_POWER_W ? offColor() : spoke.color;
}

/** Effective active: never animate a low-power (gray) component. */
function effActive(spoke: Spoke): boolean {
  return spoke.active && spoke.power >= LOW_POWER_W;
}

function cardWidth(spoke: Spoke): number {
  return spoke.imgW + CARD_EXTRA;
}

/** Card left edge in viewBox units, given its width. */
function cardLeft(geom: SlotGeom, width: number): number {
  if (geom.side === "left") return geom.anchor;
  if (geom.side === "right") return geom.anchor - width;
  return geom.anchor - width / 2;
}

function deg2rad(d: number): number {
  return (d * Math.PI) / 180;
}

/** Build a smooth cubic path from a card anchor to a radial point on the circle. */
function linePath(geom: SlotGeom, width: number): { d: string; entry: { x: number; y: number } } {
  const { y, side, angle } = geom;
  const left = cardLeft(geom, width);
  const th = deg2rad(angle);
  const entry = { x: CX + R * Math.cos(th), y: CY + R * Math.sin(th) };
  const radial = { x: Math.cos(th), y: Math.sin(th) };

  // Anchor on the card edge + initial leaving direction.
  let ax: number;
  let ay: number;
  let sdx: number;
  let sdy: number;
  if (side === "left") {
    ax = left + width;
    ay = y + CARD_H / 2;
    sdx = 1;
    sdy = 0;
  } else if (side === "right") {
    ax = left;
    ay = y + CARD_H / 2;
    sdx = -1;
    sdy = 0;
  } else {
    ax = left + width / 2;
    ay = y;
    sdx = 0;
    sdy = -1;
  }

  const d1 = 120; // leaving (straight) control distance
  const d2 = 100; // arrival (radial) control distance
  const c1x = ax + sdx * d1;
  const c1y = ay + sdy * d1;
  const c2x = entry.x + radial.x * d2;
  const c2y = entry.y + radial.y * d2;

  return {
    d: `M ${ax} ${ay} C ${c1x} ${c1y} ${c2x} ${c2y} ${entry.x} ${entry.y}`,
    entry,
  };
}

function pct(value: number, total: number): string {
  return `${(value / total) * 100}%`;
}

// Ping timing vs. power (W): grow = ring expansion time, interval = time
// between pings. Piecewise-linear through the spec points, clamped at the ends.
//   100 W -> grow 5s, interval 10s
//   2 kW  -> grow 2s, interval  3s
//   10 kW -> grow 1s, interval  2s
const PING_POINTS: Array<{ p: number; grow: number; interval: number }> = [
  { p: 100, grow: 3, interval: 10 },
  { p: 2000, grow: 2, interval: 3 },
  { p: 10000, grow: 1, interval: 2 },
];

function pingTiming(power: number): { grow: number; interval: number } {
  const pts = PING_POINTS;
  if (power <= pts[0].p) return { grow: pts[0].grow, interval: pts[0].interval };
  const last = pts[pts.length - 1];
  if (power >= last.p) return { grow: last.grow, interval: last.interval };
  for (let i = 0; i < pts.length - 1; i++) {
    const a = pts[i];
    const b = pts[i + 1];
    if (power >= a.p && power <= b.p) {
      const f = (power - a.p) / (b.p - a.p);
      return {
        grow: a.grow + (b.grow - a.grow) * f,
        interval: a.interval + (b.interval - a.interval) * f,
      };
    }
  }
  return { grow: last.grow, interval: last.interval };
}

/**
 * Build a keyframe set that expands (or contracts) the ring over `grow/interval`
 * of the timeline, then stays invisible until the cycle restarts at `interval`.
 */
function pingKeyframes(name: string, inward: boolean, grow: number, interval: number): string {
  const f = Math.min(grow / interval, 1);
  const end = Math.round(f * 10000) / 100; // % where the motion completes
  const expanded = "inset: -18px; border-radius: 34px;";
  const collapsed = "inset: 0; border-radius: 16px;";
  const start = inward ? expanded : collapsed;
  const finish = inward ? collapsed : expanded;
  return `@keyframes ${name} {
    0%        { ${start} opacity: 0.5; }
    ${end}%   { ${finish} opacity: 0; }
    100%      { ${finish} opacity: 0; }
  }`;
}

/**
 * Translucent power-usage sparkline drawn across the bottom of a card / circle.
 * The series is normalized to its own peak (fills the band vertically) and
 * stretched to span the full width via `preserveAspectRatio="none"`, so the
 * oldest sample sits on the left edge and the newest ("now") on the far right.
 */
function Sparkline({
  series,
  color,
  heightPct = "46%",
}: {
  series: number[];
  color: string;
  heightPct?: string;
}) {
  const n = series.length;
  if (n < 2) return null;
  const max = Math.max(...series);
  if (!(max > 0)) return null;

  const TOP = 4; // headroom (in the 0..100 band) so the peak never clips
  const pts = series.map((val, i) => {
    const f = Math.max(0, Math.min(1, val / max));
    const y = 100 - f * (100 - TOP);
    return `${i} ${y.toFixed(2)}`;
  });
  const area = `M 0 100 L ${pts.join(" L ")} L ${n - 1} 100 Z`;
  const line = `M ${pts.join(" L ")}`;

  return (
    // Width/height set explicitly: an <svg> is a replaced element, so relying on
    // inset-x-0 alone leaves width:auto at the SVG's intrinsic size (anchored
    // left) instead of stretching to fill the card.
    <svg
      className="absolute left-0 bottom-0 z-0 pointer-events-none"
      style={{ width: "100%", height: heightPct }}
      viewBox={`0 0 ${n - 1} 100`}
      preserveAspectRatio="none"
      aria-hidden
    >
      <path d={area} fill={color} fillOpacity={0.16} stroke="none" />
      <path
        d={line}
        fill="none"
        stroke={color}
        strokeOpacity={0.5}
        strokeWidth={1.5}
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

function SpokeCard({ spoke, series }: { spoke: Spoke; series?: number[] }) {
  const geom = SLOTS[spoke.id];
  const color = effColor(spoke);
  const active = effActive(spoke);
  const off = spoke.power < LOW_POWER_W;
  const width = cardWidth(spoke);
  const left = cardLeft(geom, width);
  // Load ("out", consuming) pings contract inward; sources ("in") radiate out.
  const inward = spoke.direction === "out";
  const { grow, interval } = pingTiming(spoke.power);
  const animName = `pf-ping-${spoke.id}`;

  return (
    <div
      className="absolute"
      style={{
        left: pct(left, VW),
        top: pct(geom.y, VH),
        width: pct(width, VW),
        height: pct(CARD_H, VH),
      }}
    >
      {/* ping rings */}
      {active && (
        <>
          <style>{pingKeyframes(animName, inward, grow, interval)}</style>
          <div
            className="absolute inset-0 rounded-2xl pointer-events-none"
            style={{
              border: `2px solid ${color}`,
              animation: `${animName} ${interval}s linear infinite`,
            }}
          />
          <div
            className="absolute inset-0 rounded-2xl pointer-events-none"
            style={{
              border: `2px solid ${color}`,
              animation: `${animName} ${interval}s linear infinite`,
              animationDelay: `${interval / 2}s`,
            }}
          />
        </>
      )}

      {/* card body */}
      <div
        className="relative h-full w-full rounded-2xl bg-panel/80 backdrop-blur flex items-center overflow-hidden"
        style={{
          border: `3px solid ${color}`,
          boxShadow: `inset 6px 6px 14px ${color}55, inset -2px -2px 8px ${color}22`,
          paddingLeft: "10px",
        }}
      >
        {/* translucent usage history (large view only) */}
        {series && <Sparkline series={series} color={color} />}
        {/* image: 10px top/bottom inset, left-aligned, width driven by height */}
        <div
          className="relative z-10 shrink-0 self-stretch flex items-center justify-start"
          style={{
            paddingTop: "10px",
            paddingBottom: "10px",
            filter: off ? offFilter() : undefined,
          }}
        >
          {spoke.icon}
        </div>
        {/* info text: starts 10px after the image right edge */}
        <div className="relative z-10 min-w-0 flex-1" style={{ marginLeft: "10px" }}>
          <div className="text-[12px] font-medium tracking-[0.15em] text-muted truncate">
            {spoke.title}
          </div>
          <div
            className="font-semibold leading-tight text-[26px]"
            style={{ color }}
          >
            {spoke.value}
          </div>
          <div
            className="text-[11px] font-medium tracking-[0.12em] uppercase truncate"
            style={{ color }}
          >
            {spoke.action}
          </div>
          {spoke.extra && (
            <div className="text-[11px] text-muted truncate">{spoke.extra}</div>
          )}
        </div>
      </div>

      {/* status badge (e.g. optimization active / grid feed-in blocked) */}
      {spoke.badge && (
        <div className="absolute z-20" style={{ top: -16, right: -16 }}>
          {spoke.badge}
        </div>
      )}
    </div>
  );
}

export default function PowerFlowGraphic({ spokes, inverter, history }: PowerFlowGraphicProps) {
  const invColor = inverter.color ?? FLOW_COLORS.blue;
  const invAnimate = inverter.animate ?? true;

  // Render the graphic once at its natural pixel size (VW x VH) with fixed
  // fonts/spacing, then uniformly scale it to fit the container width. This
  // keeps text, images and lines perfectly proportional at any size (mobile
  // included) instead of crushing card text at small widths.
  const wrapRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const update = () => setScale(el.clientWidth / VW);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div ref={wrapRef} className="relative w-full" style={{ height: VH * scale }}>
      <div
        className="absolute top-0 left-0"
        style={{
          width: VW,
          height: VH,
          transform: `scale(${scale})`,
          transformOrigin: "top left",
        }}
      >
      <style>{`
        @keyframes pf-flow { to { stroke-dashoffset: -44; } }
        @keyframes pf-ping {
          0%   { transform: scale(1);    opacity: 0.55; }
          70%  { opacity: 0; }
          100% { transform: scale(1.16); opacity: 0; }
        }
      `}</style>

      <svg
        viewBox={`0 0 ${VW} ${VH}`}
        className="absolute inset-0 h-full w-full"
        preserveAspectRatio="xMidYMid meet"
      >
        {/* power lines */}
        {spokes.map((s) => {
          const { d } = linePath(SLOTS[s.id], cardWidth(s));
          const color = effColor(s);
          const active = effActive(s);
          const reverse = s.direction === "out";
          return (
            <g key={s.id}>
              <path
                d={d}
                fill="none"
                stroke={color}
                strokeOpacity={active ? 0.85 : 0.22}
                strokeWidth={2.5}
              />
              {active && (
                <path
                  d={d}
                  fill="none"
                  stroke={color}
                  strokeWidth={3.5}
                  strokeLinecap="round"
                  strokeDasharray="9 15"
                  style={{ animation: `pf-flow 1.3s linear infinite${reverse ? " reverse" : ""}` }}
                />
              )}
            </g>
          );
        })}
      </svg>

      {/* spoke cards */}
      {spokes.map((s) => (
        <SpokeCard key={s.id} spoke={s} series={history?.[s.id]} />
      ))}

      {/* inverter ring (3px border, inner shadow + ping, matching cards) */}
      <div
        className="absolute -translate-x-1/2 -translate-y-1/2 pointer-events-none"
        style={{ left: pct(CX, VW), top: pct(CY, VH), width: pct(2 * R, VW), height: pct(2 * R, VH) }}
      >
        <div
          className="absolute inset-0 rounded-full overflow-hidden"
          style={{
            border: `3px solid ${invColor}`,
            background: "rgb(var(--c-panel) / 0.65)",
            boxShadow: `inset 6px 6px 14px ${invColor}55, inset -2px -2px 8px ${invColor}22`,
          }}
        >
          {/* translucent usage history clipped to the circle (large view only) */}
          {inverter.history && (
            <Sparkline series={inverter.history} color={invColor} heightPct="42%" />
          )}
        </div>
        <div
          className="absolute inset-0 rounded-full"
          style={{ border: `2px solid ${invColor}`, animation: invAnimate ? "pf-ping 2s ease-out infinite" : undefined }}
        />
        <div
          className="absolute inset-0 rounded-full"
          style={{ border: `2px solid ${invColor}`, animation: invAnimate ? "pf-ping 2s ease-out infinite" : undefined, animationDelay: invAnimate ? "1s" : undefined }}
        />
      </div>

      {/* inverter center */}
      <div
        className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center text-center"
        style={{ left: pct(CX, VW), top: pct(CY, VH), width: pct(R * 1.6, VW) }}
      >
        {inverter.icon && (
          <div
            className="grid place-items-center mb-1 [&>img]:!w-full [&>img]:!h-auto"
            style={{ width: "56%" }}
          >
            {inverter.icon}
          </div>
        )}
        <div className="text-[13px] tracking-[0.15em] text-muted">{inverter.label}</div>
        <div className="font-semibold text-[34px]" style={{ color: invColor }}>
          {inverter.value}
        </div>
        <div className="text-[11px] uppercase tracking-[0.12em] text-muted">
          {inverter.state}
        </div>
      </div>
    </div>
    </div>
  );
}
